import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, Alert, Snackbar, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, InputAdornment, Divider, Checkbox, Collapse
} from '@mui/material';
import { ArrowBack, Save, Payment, AccountBalance, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function RegistrarCobro() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clienteIdParam = searchParams.get('clienteId');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]); // [{ factura, monto, ret_fuente, ret_iva }]
  const [cuentasContables, setCuentasContables] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [mostrarRetenciones, setMostrarRetenciones] = useState({});

  const [cobro, setCobro] = useState({
    fecha: new Date().toISOString().split('T')[0],
    forma_pago: 'EFECTIVO',
    referencia: '',
    observaciones: '',
    cuenta_contable: '',
    cuenta_contable_id: '',
    banco_id: '',
  });

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    cargarFacturas();
    cargarCuentasContables();
    cargarBancos();
  }, []);

  // Auto-seleccionar cuenta contable según forma de pago
  useEffect(() => {
    if (cuentasContables.length === 0) return;
    if (cobro.forma_pago === 'EFECTIVO') {
      const caja = cuentasContables.find(c =>
        c.codigo === '1010101' || c.nombre?.toLowerCase().includes('caja')
      );
      if (caja) setCobro(prev => ({ ...prev, cuenta_contable: caja.codigo, cuenta_contable_id: caja.id, banco_id: '' }));
    } else {
      const banco = cuentasContables.find(c =>
        c.codigo === '1010103' || c.nombre?.toLowerCase().includes('banco')
      );
      if (banco) setCobro(prev => ({ ...prev, cuenta_contable: banco.codigo, cuenta_contable_id: banco.id }));
    }
  }, [cobro.forma_pago, cuentasContables]);

  const cargarCuentasContables = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, { headers: getHeaders() });
      const cuentas = res.data?.data || res.data || [];
      const mov = cuentas.filter(c =>
        c.acepta_movimiento && (
          c.codigo?.startsWith('101010') ||
          c.nombre?.toLowerCase().includes('caja') ||
          c.nombre?.toLowerCase().includes('banco')
        )
      );
      setCuentasContables(mov.length > 0 ? mov : cuentas.filter(c => c.acepta_movimiento));
    } catch (err) {
      console.error('Error cargando cuentas contables:', err);
    }
  };

  const cargarBancos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tesoreria/bancos`, { headers: getHeaders() });
      setBancos(res.data?.data || res.data || []);
    } catch (err) {
      console.log('Bancos no disponibles:', err.message);
    }
  };

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const [facturasRes, cobrosRes, recibosRes] = await Promise.all([
        axios.get(`${API_URL}/api/facturas`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/cuentas-cobrar/cobros`, { headers: getHeaders() }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/api/recibos`, { headers: getHeaders() }).catch(() => ({ data: { data: [] } })),
      ]);

      const todas = facturasRes.data || [];
      const recibos = recibosRes.data?.data || [];
      const cobrosExistentes = cobrosRes.data?.data || cobrosRes.data || [];

      const pendientes = todas
        .filter(f => f.estadoSRI === 'AUTORIZADO')
        .map(f => {
          const total = parseFloat(f.totales?.valorPagar || f.totales?.importeTotal || 0);
          const cobrosF = cobrosExistentes.filter(c => c.factura_id === (f.id || f.claveAcceso) || c.factura_clave === f.claveAcceso);
          const cobrado = cobrosF.reduce((s, c) => {
            return s + parseFloat(c.monto || 0) + parseFloat(c.ret_fuente_monto || 0) + parseFloat(c.ret_iva_monto || 0);
          }, 0);
          return { ...f, total, cobrado, saldo: Math.max(0, total - cobrado) };
        })
        .filter(f => f.saldo > 0.01);

      // Incluir recibos a crédito como documentos cobrables
      const FORMAS_CREDITO = ['credito_plazo', 'credito', 'Crédito', 'CREDITO'];
      const recibosPendientes = recibos
        .filter(r => r.estado !== 'ANULADO' && FORMAS_CREDITO.includes(r.forma_pago))
        .map(r => {
          const total = parseFloat(r.totales?.total || 0);
          const cobrosR = cobrosExistentes.filter(c => c.factura_id === r.id);
          const cobrado = cobrosR.reduce((s, c) =>
            s + parseFloat(c.monto || 0) + parseFloat(c.ret_fuente_monto || 0) + parseFloat(c.ret_iva_monto || 0), 0);
          return {
            id: r.id,
            tipo_documento: 'RECIBO',
            secuencial: r.numero,
            cliente: r.cliente,
            fechaEmision: r.fecha ? new Date(r.fecha).toLocaleDateString('es-EC') : '',
            totales: { valorPagar: total, importeTotal: total },
            total, cobrado, saldo: Math.max(0, total - cobrado),
          };
        })
        .filter(r => r.saldo > 0.01);

      setFacturasPendientes([...pendientes, ...recibosPendientes]);

      // Auto-seleccionar si viene clienteId
      if (clienteIdParam) {
        const filtradas = pendientes.filter(f =>
          f.cliente?.ruc === clienteIdParam || f.cliente?.id === clienteIdParam
        );
        if (filtradas.length > 0) {
          toggleSeleccion(filtradas[0], pendientes);
        }
      }
    } catch (err) {
      console.error('Error cargando facturas:', err);
      setSnackbar({ open: true, message: 'Error al cargar facturas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Alternar selección de una factura
  const toggleSeleccion = (factura, listaBase = facturasPendientes) => {
    const yaSeleccionada = facturasSeleccionadas.find(fs => fs.factura_id === (factura.id || factura.claveAcceso));
    if (yaSeleccionada) {
      setFacturasSeleccionadas(prev => prev.filter(fs => fs.factura_id !== (factura.id || factura.claveAcceso)));
    } else {
      setFacturasSeleccionadas(prev => [
        ...prev,
        {
          factura_id: factura.id || factura.claveAcceso,
          factura,
          monto: factura.saldo.toFixed(2),
          ret_fuente_monto: '',
          ret_iva_monto: '',
        }
      ]);
    }
  };

  const actualizarFacturaSeleccionada = (factura_id, campo, valor) => {
    setFacturasSeleccionadas(prev =>
      prev.map(fs => fs.factura_id === factura_id ? { ...fs, [campo]: valor } : fs)
    );
  };

  // Total cobrado en efectivo (va a caja/banco)
  const totalEfectivo = facturasSeleccionadas.reduce((s, fs) => s + parseFloat(fs.monto || 0), 0);
  // Total retenciones
  const totalRetFuente = facturasSeleccionadas.reduce((s, fs) => s + parseFloat(fs.ret_fuente_monto || 0), 0);
  const totalRetIVA = facturasSeleccionadas.reduce((s, fs) => s + parseFloat(fs.ret_iva_monto || 0), 0);
  const totalACancelar = totalEfectivo + totalRetFuente + totalRetIVA;

  const validar = () => {
    if (facturasSeleccionadas.length === 0) {
      setSnackbar({ open: true, message: 'Seleccione al menos una factura', severity: 'error' });
      return false;
    }
    if (!cobro.cuenta_contable) {
      setSnackbar({ open: true, message: 'Seleccione una cuenta contable', severity: 'error' });
      return false;
    }
    for (const fs of facturasSeleccionadas) {
      const efectivo = parseFloat(fs.monto || 0);
      const retF = parseFloat(fs.ret_fuente_monto || 0);
      const retIVA = parseFloat(fs.ret_iva_monto || 0);
      const total = efectivo + retF + retIVA;
      if (total <= 0) {
        setSnackbar({ open: true, message: 'El monto a cancelar debe ser mayor a 0', severity: 'error' });
        return false;
      }
      if (total > fs.factura.saldo + 0.01) {
        setSnackbar({ open: true, message: `El total a cancelar excede el saldo de la factura ${String(fs.factura.secuencial || '').padStart(9, '0')}`, severity: 'error' });
        return false;
      }
    }
    return true;
  };

  const guardarCobro = async () => {
    if (!validar()) return;
    setSaving(true);
    try {
      const cuentaSel = cuentasContables.find(c => c.codigo === cobro.cuenta_contable);
      const bancoSel = bancos.find(b => b.id === cobro.banco_id);

      // Si solo hay una factura, usar endpoint simple; si hay varias, usar /multiple
      if (facturasSeleccionadas.length === 1) {
        const fs = facturasSeleccionadas[0];
        await axios.post(`${API_URL}/api/cuentas-cobrar/cobros`, {
          factura_id: fs.factura_id,
          cliente_id: fs.factura.cliente?.id || fs.factura.cliente?.ruc,
          cliente_nombre: fs.factura.cliente?.nombre || fs.factura.cliente?.razonSocial,
          factura_secuencial: String(fs.factura.secuencial || '').padStart(9, '0'),
          total_factura: fs.factura.total,
          monto: parseFloat(fs.monto),
          ret_fuente_monto: parseFloat(fs.ret_fuente_monto || 0),
          ret_iva_monto: parseFloat(fs.ret_iva_monto || 0),
          fecha: cobro.fecha,
          forma_pago: cobro.forma_pago,
          referencia: cobro.referencia,
          observaciones: cobro.observaciones,
          cuenta_contable: cobro.cuenta_contable_id || cobro.cuenta_contable,
          banco_id: cobro.banco_id,
          banco_nombre: bancoSel?.nombre || '',
        }, { headers: getHeaders() });
      } else {
        await axios.post(`${API_URL}/api/cuentas-cobrar/cobros/multiple`, {
          cliente_id: facturasSeleccionadas[0]?.factura?.cliente?.id,
          cliente_nombre: facturasSeleccionadas[0]?.factura?.cliente?.nombre,
          fecha: cobro.fecha,
          forma_pago: cobro.forma_pago,
          referencia: cobro.referencia,
          observaciones: cobro.observaciones,
          cuenta_contable: cobro.cuenta_contable_id || cobro.cuenta_contable,
          banco_id: cobro.banco_id,
          ret_fuente_cuenta: null,
          ret_iva_cuenta: null,
          facturas: facturasSeleccionadas.map(fs => ({
            factura_id: fs.factura_id,
            monto: parseFloat(fs.monto),
            ret_fuente_monto: parseFloat(fs.ret_fuente_monto || 0),
            ret_iva_monto: parseFloat(fs.ret_iva_monto || 0),
            total_factura: fs.factura.total,
          }))
        }, { headers: getHeaders() });
      }

      setSnackbar({ open: true, message: 'Cobro registrado exitosamente. Asiento contable generado.', severity: 'success' });
      setFacturasSeleccionadas([]);
      setCobro(prev => ({ ...prev, referencia: '', observaciones: '' }));
      cargarFacturas();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || err.response?.data?.error || 'Error al registrar cobro', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const mostrarBancos = ['TRANSFERENCIA', 'CHEQUE', 'TARJETA', 'DEPOSITO'].includes(cobro.forma_pago);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/cuentas-cobrar')}><ArrowBack /></IconButton>
        <Payment color="primary" />
        <Typography variant="h5" fontWeight={700}>Registrar Cobro</Typography>
      </Box>

      {facturasPendientes.length === 0 ? (
        <Alert severity="info">No hay facturas con saldo pendiente.</Alert>
      ) : (
        <>
          {/* ===== TABLA DE FACTURAS PENDIENTES ===== */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: '#1976d2', color: '#fff' }}>
              <Typography variant="h6" fontWeight={700}>
                Facturas con Saldo Pendiente ({facturasPendientes.length})
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Seleccione una o más facturas. Si el cliente realizó retenciones, expanda la fila para ingresarlas.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell padding="checkbox" />
                    <TableCell><strong>Factura</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>Cobrado</strong></TableCell>
                    <TableCell align="right"><strong>Saldo</strong></TableCell>
                    <TableCell align="right"><strong>Monto Cobrar</strong></TableCell>
                    <TableCell align="center"><strong>Ret. Cliente</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturasPendientes.map(f => {
                    const fid = f.id || f.claveAcceso;
                    const sel = facturasSeleccionadas.find(fs => fs.factura_id === fid);
                    const verRet = mostrarRetenciones[fid];

                    return (
                      <>
                        <TableRow key={fid} hover selected={!!sel}
                          sx={{ cursor: 'pointer', bgcolor: sel ? '#e3f2fd' : 'inherit' }}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={!!sel} onChange={() => toggleSeleccion(f)} />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {String(f.secuencial || '').padStart(9, '0')}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{f.cliente?.nombre || f.cliente?.razonSocial || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">{f.cliente?.ruc || ''}</Typography>
                          </TableCell>
                          <TableCell>{f.fechaEmision}</TableCell>
                          <TableCell align="right">${f.total.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>${f.cobrado.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <strong style={{ color: '#d32f2f' }}>${f.saldo.toFixed(2)}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {sel && (
                              <TextField type="number" size="small"
                                value={sel.monto}
                                onChange={e => actualizarFacturaSeleccionada(fid, 'monto', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                inputProps={{ min: 0, max: f.saldo, step: 0.01 }}
                                sx={{ width: 110 }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {sel && (
                              <IconButton size="small" onClick={() => setMostrarRetenciones(prev => ({ ...prev, [fid]: !verRet }))}>
                                {verRet ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Fila expandible para retenciones */}
                        {sel && verRet && (
                          <TableRow key={`ret-${fid}`} sx={{ bgcolor: '#fffde7' }}>
                            <TableCell colSpan={9} sx={{ py: 1, px: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 200 }}>
                                  💡 El cliente realizó retenciones sobre esta factura:
                                </Typography>
                                <TextField size="small" label="Ret. Renta (IR)"
                                  type="number"
                                  value={sel.ret_fuente_monto}
                                  onChange={e => actualizarFacturaSeleccionada(fid, 'ret_fuente_monto', e.target.value)}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 140 }}
                                  helperText="Crédito tributario IR" />
                                <TextField size="small" label="Ret. IVA"
                                  type="number"
                                  value={sel.ret_iva_monto}
                                  onChange={e => actualizarFacturaSeleccionada(fid, 'ret_iva_monto', e.target.value)}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 140 }}
                                  helperText="Crédito tributario IVA" />
                                {(parseFloat(sel.ret_fuente_monto || 0) + parseFloat(sel.ret_iva_monto || 0)) > 0 && (
                                  <Chip size="small" color="info"
                                    label={`Total cancelado: $${(parseFloat(sel.monto || 0) + parseFloat(sel.ret_fuente_monto || 0) + parseFloat(sel.ret_iva_monto || 0)).toFixed(2)}`} />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totales selección */}
            {facturasSeleccionadas.length > 0 && (
              <Box sx={{ p: 2, bgcolor: '#e3f2fd', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight={700}>
                  {facturasSeleccionadas.length} factura(s) seleccionada(s)
                </Typography>
                <Typography variant="body2">Efectivo/Transferencia: <strong style={{ color: '#1976d2' }}>${totalEfectivo.toFixed(2)}</strong></Typography>
                {totalRetFuente > 0 && (
                  <Typography variant="body2">Ret. IR: <strong style={{ color: '#7b1fa2' }}>${totalRetFuente.toFixed(2)}</strong></Typography>
                )}
                {totalRetIVA > 0 && (
                  <Typography variant="body2">Ret. IVA: <strong style={{ color: '#7b1fa2' }}>${totalRetIVA.toFixed(2)}</strong></Typography>
                )}
                <Typography variant="h6" fontWeight={700} sx={{ ml: 'auto', color: '#1976d2' }}>
                  Total a Cancelar: ${totalACancelar.toFixed(2)}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* ===== FORMULARIO DE COBRO ===== */}
          {facturasSeleccionadas.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Datos del Cobro</Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth required type="date" label="Fecha"
                    value={cobro.fecha}
                    onChange={e => setCobro({ ...cobro, fecha: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Forma de Pago</InputLabel>
                    <Select value={cobro.forma_pago} label="Forma de Pago"
                      onChange={e => setCobro({ ...cobro, forma_pago: e.target.value })}>
                      <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                      <MenuItem value="TRANSFERENCIA">Transferencia Bancaria</MenuItem>
                      <MenuItem value="CHEQUE">Cheque</MenuItem>
                      <MenuItem value="DEPOSITO">Depósito</MenuItem>
                      <MenuItem value="TARJETA">Tarjeta de Crédito/Débito</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Referencia / No. Comprobante"
                    value={cobro.referencia}
                    onChange={e => setCobro({ ...cobro, referencia: e.target.value })} />
                </Grid>

                <Grid item xs={12}><Divider><Chip label="Contabilidad" icon={<AccountBalance />} size="small" /></Divider></Grid>

                <Grid item xs={12} sm={mostrarBancos ? 6 : 12}>
                  <FormControl fullWidth required>
                    <InputLabel>Cuenta Contable (Destino del dinero)</InputLabel>
                    <Select value={cobro.cuenta_contable} label="Cuenta Contable (Destino del dinero)"
                      onChange={e => {
                        const c = cuentasContables.find(x => x.codigo === e.target.value);
                        setCobro({ ...cobro, cuenta_contable: e.target.value, cuenta_contable_id: c?.id || e.target.value });
                      }}>
                      {cuentasContables.map(c => (
                        <MenuItem key={c.id || c.codigo} value={c.codigo}>
                          {c.codigo} - {c.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {cobro.forma_pago === 'EFECTIVO' ? '💰 Efectivo → Caja' : '🏦 → Cuenta bancaria'}
                    {(totalRetFuente + totalRetIVA) > 0 &&
                      ' · Las retenciones van a cuentas de Crédito Tributario automáticamente'}
                  </Typography>
                </Grid>

                {mostrarBancos && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Banco</InputLabel>
                      <Select value={cobro.banco_id} label="Banco"
                        onChange={e => setCobro({ ...cobro, banco_id: e.target.value })}>
                        <MenuItem value="">— Sin especificar —</MenuItem>
                        {bancos.map(b => (
                          <MenuItem key={b.id} value={b.id}>
                            {b.nombre} {b.numero_cuenta ? `(${b.numero_cuenta})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField fullWidth label="Observaciones"
                    value={cobro.observaciones}
                    onChange={e => setCobro({ ...cobro, observaciones: e.target.value })}
                    multiline rows={2} />
                </Grid>

                {/* Resumen del asiento que se generará */}
                {(totalRetFuente + totalRetIVA) > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<AccountBalance />}>
                      <strong>Asiento que se generará:</strong>
                      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                        <li>DEBE Caja/Banco: ${totalEfectivo.toFixed(2)}</li>
                        {totalRetFuente > 0 && <li>DEBE Ret. IR Recibida (1010403): ${totalRetFuente.toFixed(2)}</li>}
                        {totalRetIVA > 0 && <li>DEBE Ret. IVA Recibida (1010402): ${totalRetIVA.toFixed(2)}</li>}
                        <li>HABER CxC Clientes: ${totalACancelar.toFixed(2)}</li>
                      </Box>
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => navigate('/cuentas-cobrar')} disabled={saving}>
                      Cancelar
                    </Button>
                    <Button variant="contained" size="large"
                      startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                      onClick={guardarCobro} disabled={saving}>
                      {saving ? 'Registrando...' : 'Registrar Cobro'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
        </>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
